import { useEffect, useState } from 'react';

const LARS_STAGES = [
  { value: 1, label: '1 — Data Preparation' },
  { value: 2, label: '2 — Query Execution' },
  { value: 3, label: '3 — Scoring' },
  { value: 4, label: '4 — Reporting' },
];

export interface RunLarsStagesModalProps {
  isOpen: boolean;
  title?: string;
  defaultStages?: number[];
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (stages: number[]) => void;
}

export default function RunLarsStagesModal({
  isOpen,
  title = 'Select stages to run',
  defaultStages = [1, 2, 3, 4],
  isSubmitting = false,
  onClose,
  onConfirm,
}: RunLarsStagesModalProps) {
  const [selectedStages, setSelectedStages] = useState<number[]>(defaultStages);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedStages(defaultStages.length ? [...defaultStages].sort((a, b) => a - b) : [1, 2, 3, 4]);
      setLocalError(null);
    }
  }, [isOpen, defaultStages]);

  if (!isOpen) return null;

  const toggleStage = (stage: number) => {
    setSelectedStages((prev) =>
      prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage].sort((a, b) => a - b),
    );
  };

  const handleConfirm = () => {
    if (selectedStages.length === 0) {
      setLocalError('Select at least one stage.');
      return;
    }
    onConfirm(selectedStages);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="fixed inset-0 bg-black/40" onClick={isSubmitting ? undefined : onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose which Labs stages to run for this execution. Later stages are skipped if an earlier one fails.
          </p>
          <div className="space-y-2 border rounded px-3 py-3 bg-gray-50 mb-4">
            {LARS_STAGES.map((stage) => (
              <label key={stage.value} className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={selectedStages.includes(stage.value)}
                  onChange={() => toggleStage(stage.value)}
                  disabled={isSubmitting}
                />
                {stage.label}
              </label>
            ))}
          </div>
          {localError && <p className="text-sm text-red-600 mb-3">{localError}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Starting…' : 'Run selected stages'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
